import { Button, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import { PureComponent } from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';

import styles from './replyStatus.module.scss';

type ReplyStatusProps = {
  /** Extra classnames */
  className?: string;
  /** Show closed state */
  isClosed?: boolean;
  /** Not your conversation */
  isNotYours?: boolean;
  /** Deprecated: Is assigned */
  isAssigned?: boolean;
  /** Show Claim Button */
  showClaimButton?: boolean;
  /** Show Takeover Button */
  showTakeoverButton?: boolean;
  /** On claim callback */
  onClaim?: () => void;
  /** On call callback */
  onCall?: () => void;
  /** On compose email callback */
  onCompose?: () => void;
  /** Custom text */
  customText?: string;
  /** Explanation */
  explanation?: JSX.Element;
};

export class ReplyStatus extends PureComponent<ReplyStatusProps> {
  componentDidMount() {
    // warn about deprecated props
    const { isAssigned } = this.props;
    if (isAssigned) {
      console.warn('Warning: Prop isAssigned is deprecated on ReplyStatus.');
    }
  }

  render() {
    const {
      className,
      isClosed,
      isNotYours,
      isAssigned,
      showClaimButton,
      showTakeoverButton,
      onClaim,
      customText,
      explanation,
      onCall,
      onCompose,
      ...otherProps
    } = this.props;

    const rootClass = classnames(
      {
        [styles.root]: true,
      },
      className,
    );

    const hasClaimAccess = accessControl.hasAccess('conversation.claim');

    let statusText = typeof customText === 'string' ? customText : undefined;
    let showClaim =
      typeof showClaimButton === 'boolean' ? showClaimButton : false;
    let showTakeOver =
      typeof showTakeoverButton === 'boolean' ? showTakeoverButton : false;
    if (isClosed) {
      statusText = !statusText ? 'This conversation is closed' : statusText;
    } else if (isNotYours) {
      statusText = !statusText
        ? 'You need to be assigned to reply'
        : statusText;
      if (!showClaim && !showTakeOver) {
        showClaim = !isAssigned;
        showTakeOver = Boolean(isAssigned);
      }
    }

    const hasOtherActions = onCall || onCompose;

    const claimTooltipContent = (label: string) => (
      <CommandKeybindingsTooltipContent commandName="conversation:claim">
        {label}
      </CommandKeybindingsTooltipContent>
    );

    return (
      <div {...otherProps} className={rootClass}>
        {explanation && <h2 className={styles.explanation}>{explanation}</h2>}
        <h2 className={styles.title}>{statusText}</h2>
        {showClaim && (
          <Tooltip
            content={
              hasClaimAccess
                ? claimTooltipContent('Claim conversation')
                : "You don't have permissions to claim conversation"
            }
          >
            <Button onClick={onClaim} disabled={!hasClaimAccess}>
              Claim conversation
            </Button>
          </Tooltip>
        )}
        {showTakeOver && (
          <Tooltip
            content={
              hasClaimAccess
                ? claimTooltipContent('Take over conversation')
                : "You don't have permissions to take over conversation"
            }
          >
            <Button onClick={onClaim} disabled={!hasClaimAccess}>
              Take over conversation
            </Button>
          </Tooltip>
        )}
        {hasClaimAccess && (
          <>
            {(showClaim || showTakeOver) && hasOtherActions && (
              <span className={styles.actionDivider}>Or</span>
            )}
            <div className={styles.actions}>
              {onCall && (
                <Button
                  variant="secondary"
                  className={styles.action}
                  onClick={onCall}
                >
                  Make call
                </Button>
              )}
              {onCompose && (
                <Button
                  variant="secondary"
                  className={styles.action}
                  onClick={onCompose}
                >
                  Compose an email
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}
